'use client'

import React, { useEffect, useRef, useState } from 'react'

/**
 * Craft-themed location map (Google Maps JavaScript API).
 *
 * - Custom map style in the Craft palette (teal water, warm-cream land, muted
 *   roads, hidden POI clutter) + a teal marker.
 * - Lazy-mounted (IntersectionObserver) so the ~800 KB Maps SDK stays off the
 *   initial load / critical path.
 * - Degrades gracefully: if there's no key, no coordinates, or the SDK fails to
 *   authorize (e.g. referer not allow-listed), it renders the existing Google
 *   embed iframe (lightly filtered) so a map always shows.
 *
 * Loosely typed (no `@types/google.maps` dependency) — the SDK is accessed via
 * the runtime `google.maps` global.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

const MAP_STYLE: Array<Record<string, unknown>> = [
  { elementType: 'geometry', stylers: [{ color: '#ece7da' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5e5a50' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f4f0e6' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cdc6b4' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#e6e0d0' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#cdd9c5' }, { visibility: 'on' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e1dac8' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a857a' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#d8cdb2' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#c3b692' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#009BAB' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#eaf6f7' }] },
]

const FALLBACK_FILTER = 'sepia(0.22) saturate(0.75) hue-rotate(325deg) brightness(1.06) contrast(0.9)'

/** Singleton Maps SDK loader — load the script at most once per page. */
let mapsPromise: Promise<any> | null = null
let authFailed = false
const authFailListeners = new Set<() => void>()

/**
 * Google calls `gm_authFailure` when the key is rejected — invalid key,
 * disallowed referer, the Maps JS API not enabled, or (commonly) billing/quota
 * problems that only surface at tile-render time. That render-time failure can
 * fire AFTER the SDK load promise has already resolved, so we can't model it by
 * rejecting that promise. Instead we broadcast to every mounted map so each one
 * can swap to the themed embed fallback instead of showing Google's gray error.
 */
function handleAuthFailure() {
  authFailed = true
  authFailListeners.forEach((fn) => {
    try {
      fn()
    } catch {
      /* noop */
    }
  })
}

function loadMaps(apiKey: string): Promise<any> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'))
  const w = window as any
  w.gm_authFailure = handleAuthFailure
  if (w.google?.maps?.Map) return Promise.resolve(w.google.maps)
  if (mapsPromise) return mapsPromise
  mapsPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async`
    s.async = true
    s.onload = () => (w.google?.maps?.Map ? resolve(w.google.maps) : reject(new Error('maps unavailable')))
    s.onerror = () => reject(new Error('maps script load error'))
    document.head.appendChild(s)
  })
  return mapsPromise
}

type Props = {
  /** Project / location coordinates. When absent, the iframe fallback is used. */
  lat?: number | null
  lng?: number | null
  zoom?: number
  /** Themed Google embed URL used as the no-key / failure fallback. */
  embedSrc: string
  className?: string
  title?: string
}

export function CraftMap({ lat, lng, zoom = 13, embedSrc, className, title = 'Location map' }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)
  const [failed, setFailed] = useState(false)
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
  const canRenderJs = Boolean(apiKey) && typeof lat === 'number' && typeof lng === 'number'

  // Mount the SDK only when the map nears the viewport.
  useEffect(() => {
    if (!canRenderJs || inView) return
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true)
          io.disconnect()
        }
      },
      { rootMargin: '400px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [canRenderJs, inView])

  // Initialize the styled map + teal marker.
  useEffect(() => {
    if (!inView || failed || !canRenderJs || !apiKey) return
    let cancelled = false
    let tileTimer: ReturnType<typeof setTimeout> | undefined
    const onAuthFail = () => {
      if (!cancelled) setFailed(true)
    }
    authFailListeners.add(onAuthFail)
    // NOTE: a previously-rejected key is handled inside loadMaps().then() below
    // (async setFailed) — no synchronous state write needed here.
    loadMaps(apiKey)
      .then((maps: any) => {
        if (cancelled || !ref.current) return
        if (authFailed) {
          setFailed(true)
          return
        }
        const center = { lat: lat as number, lng: lng as number }
        const map = new maps.Map(ref.current, {
          center,
          zoom,
          styles: MAP_STYLE,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
          gestureHandling: 'cooperative',
        })
        new maps.Marker({
          position: center,
          map,
          title,
          icon: {
            path: 'M12 0C7 0 3 4 3 9c0 6.6 9 15 9 15s9-8.4 9-15c0-5-4-9-9-9z',
            fillColor: '#009BAB',
            fillOpacity: 1,
            strokeColor: '#00727f',
            strokeWeight: 1.5,
            scale: 1.7,
            anchor: new maps.Point(12, 24),
          },
        })
        // Safety net: if tiles never load (auth/billing rejected at render time),
        // Google paints its own gray "couldn't load" error. Detect the missing
        // `tilesloaded` event and swap to the themed embed instead. Skipped while
        // the tab is hidden, where tiles legitimately don't paint.
        let loaded = false
        maps.event.addListenerOnce(map, 'tilesloaded', () => {
          loaded = true
        })
        tileTimer = setTimeout(() => {
          if (!cancelled && !loaded && !document.hidden) setFailed(true)
        }, 6000)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
      authFailListeners.delete(onAuthFail)
      if (tileTimer) clearTimeout(tileTimer)
    }
  }, [inView, failed, canRenderJs, apiKey, lat, lng, zoom, title])

  // Fallback: themed Google embed (always works, no key needed).
  if (!canRenderJs || failed) {
    return (
      <iframe
        className={className}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        src={embedSrc}
        title={title}
        style={{ filter: FALLBACK_FILTER }}
      />
    )
  }

  return (
    <div ref={ref} className={className} role="img" aria-label={title} style={{ backgroundColor: '#ece7da' }} />
  )
}
